import { DISQUALIFIED_BAND, FIT_BANDS } from "./constant";

/**
 *
 * Get band object from FIT_BANDS by companring score with trait attributes
 * upperBound: ub
 * lowerBound: lb
 * @param {object} ({ub: number, lb: number, median: number, score: number})
 * @returns {object} {label: string, color: string, textColor: string}
 */
export const getTraitBand = ({ ub = 0, lb = 0, median = 0, score = 0, disqualified=false }) => {
  if (disqualified) return DISQUALIFIED_BAND;
  if (score >= ub) return FIT_BANDS.HIGHLY;
  else if (score <= ub && score >= median) return FIT_BANDS.RECOMMENDED;
  else if (score <= median && score >= lb) return FIT_BANDS.CAUTIOUSLY;
  else return FIT_BANDS.NOT;
};

/**
 *
 * Final PASS/FAIL verdict for a report, shared by the header pill and the
 * Overall Summary so the two can never disagree. Caller decides visibility --
 * this only runs where a cutoff applies (reportData.cutOffCleared != null).
 * @param {object} reportData
 * @param {object} positionConfig
 * @returns {string} "PASS" | "FAIL" | "PENDING"
 */
export const getOverallVerdict = (reportData, positionConfig) => {
  const isFail = reportData?.cutOffCleared === false || reportData?.disqualified;
  if (isFail) return "FAIL";
  // No trust score is generated when AI proctoring is off, so don't wait for one.
  // Until positionConfig loads, keep waiting to avoid flashing PASS on proctored positions.
  const isAiProctoringOff = !!positionConfig && !positionConfig.aiProctoring;
  if (isAiProctoringOff) return "PASS";
  const trustScoreGenerated = !!reportData?.trustScore?.remark;
  if (!trustScoreGenerated) return "PENDING";
  return "PASS";
};

/**
 *
 * Get trait style object by FIT_BANDS band
 * @param {object} band ({label: string, color: string, textColor: string})
 * @returns {object} {background: string, color: string}
 */
export const convertToStyle = (band) => ({
  background: band.color,
  color: band.textColor,
});

/**
 *
 * Get array of objects from object of single array
 * @param {object} obj {traitId:string : [data: object], ...}
 * @returns {array} [{...data, traitId}: object, ...]
 */
export const generateList = (obj) => {
  return Object.keys(obj).reduce(
    (acc, traitId) =>
      obj[traitId].length > 0 ? [...acc, { ...obj[traitId][0], traitId }] : acc,
    []
  );
};


/**
 * Converts a display name like "Honesty-Humility", "Social Self-Esteem",
 * "Openness to Experience" to camelCase: honestyHumility, socialSelfEsteem,
 * opennessToExperience.
 */
export function toCamelCase(str) {
  return str
      .split(/[\s-]+/)
      .map((word, i) =>
          i === 0
              ? word.toLowerCase()
              : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join('');
}

/**
* Transforms an array of trait objects into the structured input shape
* expected by the Personality Profiler prompt.
*
* Input:  Array of { name, score, parentTrait, isParentTrait, ... }
* Output: {
*   honestyHumility: { score: N, sincerity: N, fairness: N, ... },
*   emotionality:    { score: N, fearfulness: N, anxiety: N, ... },
*   ...
* }
*
* Parent score = average of its sub-trait scores, rounded to 2 decimals.
*/
export function transformTraitsToProfilerInput(traits) {
  const subTraits = traits.filter((t) => !t.isParentTrait);

  // Group sub-traits by parent
  const groups = {};
  for (const trait of subTraits) {
      const parentKey = toCamelCase(trait.parentTrait);
      if (!groups[parentKey]) groups[parentKey] = [];
      groups[parentKey].push(trait);
  }

  const result = {};
  for (const [parentKey, children] of Object.entries(groups)) {
      const avg = children.reduce((sum, t) => sum + t.score, 0) / children.length;
      result[parentKey] = {
          score: +avg.toFixed(2),
      };
      for (const child of children) {
          result[parentKey][toCamelCase(child.name)] = child.score;
      }
  }

  return result;
}